import { Link, useNavigate } from '@tanstack/react-router';
import { invoice, openLink } from '@telegram-apps/sdk-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sendCrashReport } from '@app/logger/crashReport';

import CheckIcon from '@common/assets/icons/check.svg?react';
import ChevroneRightIcon from '@common/assets/icons/chevrone_right.svg?react';
import StarIcon from '@common/assets/icons/star.svg?react';
import { useTabbar } from '@common/providers/useTabbar';
import { appInstanceService } from '@common/services/appInstanceService';
import { BottomSheet } from '@common/ui/BottomSheet/BottomSheet';
import { Button } from '@common/ui/buttons/Button/Button';
import { CachedImage } from '@common/ui/CachedImage';
import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Heading } from '@common/ui/typography/Heading';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo, postEvent } from '@common/utils/fetcher';
import { OFERTA_URL } from '@common/utils/legalLinks';
import { safeTma } from '@common/utils/safeTma';

import { useAccountStore } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';

import { analytics } from '@services/AnalyticsService';
import { usePostAuth } from '@services/hooks/AuthService';
import {
  useGetPaymentsCatalog,
  useGetPaymentsSubscriptionsCurrent,
  usePostPaymentsCheckoutSessions,
} from '@services/hooks/PaymentsService';
import {
  type BillingPrice,
  type BillingProduct,
  type CheckoutPaymentMethodType,
  type CheckoutSession,
  getPaymentsCheckoutSessionsById,
} from '@services/PaymentsService';

import {
  blocksNewRecurringPurchase,
  isBillingSubscriptionVisible,
} from '@widgets/payments/utils';

import { AdvantageCard, PaymentMethodCard, TariffCard } from './components';

const ACTIVE_CHECKOUT_SESSION_STORAGE_KEY = 'premium_checkout_session_id';

// Paywall payment-method options. 'yookassa' is the bank-card option,
// 'sberbank' the auto-renewing SberPay one and 'sbp' the auto-renewing СБП
// one — all three create checkout sessions with provider 'yookassa' against
// the same yookassa price rows, differing only in the paymentMethodType sent
// to the checkout API.
type PremiumPaymentMethod = 'telegram_stars' | 'yookassa' | 'sberbank' | 'sbp';

// Official Sber brand green, used for the SberPay option sign (no dedicated
// logo asset in the icon set).
const SBER_GREEN = '#21A038';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const terminalStatuses = new Set([
  'succeeded',
  'failed',
  'cancelled',
  'expired',
]);

// The server rejects a duplicate purchase with 409 + one of these codes.
// Without this branch the user saw the generic "try again later" toast and
// simply retried — which can never succeed for either of them.
const SUBSCRIPTION_ALREADY_ACTIVE_CODE = 'SUBSCRIPTION_ALREADY_ACTIVE';
const SUBSCRIPTION_PURCHASE_PENDING_CODE = 'SUBSCRIPTION_PURCHASE_PENDING';

// Renders the ISO instant in the DEVICE timezone, so a period ending at 21:00Z
// reads as the next day east of UTC+3. Acceptable for a «до <date>» line.
const formatPeriodEnd = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const Premium = () => {
  const { setBottomSheet } = useTabbar();
  const emit = useEventEmit();
  const emitRef = useRef(emit);
  const navigate = useNavigate();

  const [selectedProductCode, setSelectedProductCode] =
    useState<string>('premium.monthly');
  const [isResumingCheckout, setIsResumingCheckout] = useState(false);
  // Confirmation URL of the checkout the user just started, surfaced as a real
  // anchor. This is the ONLY payment path that cannot be silently swallowed:
  // openLink posts web_app_open_link and returns void, so when the host
  // ignores the event (or the transport is inert) nothing happens and nothing
  // throws — safeTma reports success and we get no signal at all. A user tap
  // on an <a href> is plain browser navigation, with none of that surface.
  const [pendingCheckoutUrl, setPendingCheckoutUrl] = useState<string | null>(
    null,
  );
  // The user's explicit selector pick; null until they tap an option. The
  // effective paymentMethod below falls back to the best available default
  // (СБП → SberPay → card → Stars, product decision), so the preselection
  // holds without waiting for the catalog to load.
  const [chosenPaymentMethod, setChosenPaymentMethod] =
    useState<PremiumPaymentMethod | null>(null);

  const { setUser } = useAuthStore();
  const isSuperlikeEnabled = useAccountStore(
    (state) => state.account?.features.superlike === true,
  );
  const { trigger: triggerPostAuth } = usePostAuth();
  const { trigger: triggerPostPaymentsCheckoutSessions, isMutating } =
    usePostPaymentsCheckoutSessions();
  const { data: paymentCatalog } = useGetPaymentsCatalog();
  const { data: currentSubscription, mutate: refreshCurrentSubscription } =
    useGetPaymentsSubscriptionsCurrent();

  useEffect(() => {
    emitRef.current = emit;
  }, [emit]);

  const currentProvider =
    appInstanceService.getCurrentPaymentProvider() || 'telegram_stars';
  // YooKassa-backed options in the mini-app paywall: only when the server
  // gate says so AND the surface's own provider is Telegram Stars — on any
  // other surface the checkout API would reject the yookassa hint anyway.
  const cardPaymentsAvailable =
    Boolean(paymentCatalog?.cardPaymentsAvailable) &&
    currentProvider === 'telegram_stars';
  const sberpayAvailable =
    Boolean(paymentCatalog?.sberpayRecurringAvailable) &&
    currentProvider === 'telegram_stars';
  const sbpAvailable =
    Boolean(paymentCatalog?.sbpRecurringAvailable) &&
    currentProvider === 'telegram_stars';
  const isMethodAvailable = (method: PremiumPaymentMethod): boolean => {
    if (method === 'sberbank') {
      return sberpayAvailable;
    }
    if (method === 'sbp') {
      return sbpAvailable;
    }
    if (method === 'yookassa') {
      return cardPaymentsAvailable;
    }
    return true;
  };
  // Effective method: the user's pick while its option is still offered,
  // otherwise the best available default (СБП first — product decision).
  const paymentMethod: PremiumPaymentMethod =
    chosenPaymentMethod && isMethodAvailable(chosenPaymentMethod)
      ? chosenPaymentMethod
      : sbpAvailable
        ? 'sbp'
        : sberpayAvailable
          ? 'sberbank'
          : cardPaymentsAvailable
            ? 'yookassa'
            : 'telegram_stars';
  // All YooKassa-backed options pay the same yookassa price row.
  const selectedProvider =
    paymentMethod === 'sberbank' ||
    paymentMethod === 'sbp' ||
    paymentMethod === 'yookassa'
      ? 'yookassa'
      : currentProvider;
  // Sent only for selector-driven yookassa checkouts; surfaces whose own
  // provider is yookassa (web) keep their default payment page.
  const checkoutPaymentMethodType: CheckoutPaymentMethodType | undefined =
    paymentMethod === 'sberbank'
      ? 'sberbank'
      : paymentMethod === 'sbp'
        ? 'sbp'
        : paymentMethod === 'yookassa'
          ? 'bank_card'
          : undefined;
  const getSelectedPrice = useCallback(
    (product: BillingProduct): BillingPrice | undefined =>
      product.prices.find(
        (price) => price.provider === selectedProvider && price.isActive,
      ),
    [selectedProvider],
  );
  const products = useMemo(
    () =>
      (paymentCatalog?.products || []).filter(
        (product) => product.isActive && Boolean(getSelectedPrice(product)),
      ),
    [paymentCatalog, getSelectedPrice],
  );
  const selectedProduct = useMemo(
    () =>
      products.find((product) => product.productCode === selectedProductCode),
    [products, selectedProductCode],
  );

  useEffect(() => {
    analytics.trackPaywallViewed('premium_page');
  }, []);

  useEffect(() => {
    if (!products.length) {
      return;
    }

    if (
      !products.find((product) => product.productCode === selectedProductCode)
    ) {
      setSelectedProductCode(products[0].productCode);
    }
  }, [products, selectedProductCode]);

  const clearActiveCheckout = useCallback(() => {
    window.localStorage.removeItem(ACTIVE_CHECKOUT_SESSION_STORAGE_KEY);
  }, []);

  const rememberActiveCheckout = useCallback((sessionId: string) => {
    window.localStorage.setItem(ACTIVE_CHECKOUT_SESSION_STORAGE_KEY, sessionId);
  }, []);

  const refreshAuthState = useCallback(async () => {
    const data = await triggerPostAuth();
    if (data?.user) {
      setUser(data.user);
    }
  }, [setUser, triggerPostAuth]);

  const pollCheckoutSession = useCallback(async (sessionId: string) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < 3 * 60 * 1000) {
      const session = await getPaymentsCheckoutSessionsById({ sessionId });
      if (terminalStatuses.has(session.status)) {
        return session;
      }

      const elapsed = Date.now() - startedAt;
      await sleep(elapsed < 60 * 1000 ? 1000 : 5000);
    }

    return getPaymentsCheckoutSessionsById({ sessionId });
  }, []);

  const handleTerminalCheckout = useCallback(
    async (session: CheckoutSession) => {
      if (session.status === 'succeeded') {
        clearActiveCheckout();
        await refreshAuthState();
        navigate({ to: '/profile' });
        emitRef.current('info', {
          title: 'Успешно',
          description: 'Вы успешно подключили тариф',
        });
        return;
      }

      if (
        session.status === 'processing' ||
        session.status === 'awaiting_user_action'
      ) {
        return;
      }

      clearActiveCheckout();
      emitRef.current('error', {
        title: 'Ошибка',
        description:
          session.failureCode ||
          'Платеж не завершился. Попробуйте еще раз позже',
      });
    },
    [clearActiveCheckout, navigate, refreshAuthState],
  );

  const resumePendingCheckout = useCallback(
    async (sessionId: string) => {
      try {
        // ONE check here, never the 3-minute loop. This runs on EVERY app open
        // that finds a stored session id, and handleTerminalCheckout returns
        // early for a non-terminal session WITHOUT clearing that id — so a
        // checkout that was created but never paid (the normal state when the
        // confirmation page fails to open) re-armed `isResumingCheckout` on
        // every open and held the buy button in `loading` for three minutes
        // each time. The paywall was hostage to exactly the failure the user
        // was trying to escape: one un-openable checkout and Премиум looks
        // permanently dead.
        const session = await getPaymentsCheckoutSessionsById({ sessionId });

        if (terminalStatuses.has(session.status)) {
          await handleTerminalCheckout(session);
          return;
        }
      } finally {
        // Release the button before any polling — a background watch must
        // never gate the user's ability to start a new purchase.
        setIsResumingCheckout(false);
      }

      // Still pending: keep watching in the BACKGROUND, because the user may
      // have paid in an external browser and come back before the webhook
      // landed. Same information as before, without holding the UI.
      await handleTerminalCheckout(await pollCheckoutSession(sessionId));
    },
    [handleTerminalCheckout, pollCheckoutSession],
  );

  useEffect(() => {
    const sessionIdFromUrl = new URLSearchParams(window.location.search).get(
      'checkoutSessionId',
    );
    const storedSessionId = window.localStorage.getItem(
      ACTIVE_CHECKOUT_SESSION_STORAGE_KEY,
    );
    const sessionId = sessionIdFromUrl || storedSessionId;

    if (!sessionId) {
      return;
    }

    setIsResumingCheckout(true);
    void resumePendingCheckout(sessionId);
  }, [resumePendingCheckout]);

  const onTariffSelect = useCallback(
    (productCode: string) => {
      setSelectedProductCode(productCode);
      const selectedProduct = products.find(
        (product) => product.productCode === productCode,
      );
      if (!selectedProduct) {
        return;
      }

      analytics.trackPlanSelected(
        productCode,
        `${selectedProduct.durationDays}_days`,
      );
    },
    [products],
  );

  const onBuy = useCallback(
    async (productCode: string) => {
      try {
        const selectedProduct = products.find(
          (product) => product.productCode === productCode,
        );
        if (!selectedProduct) {
          throw new Error('Selected product is not available');
        }

        const selectedPrice = getSelectedPrice(selectedProduct);
        if (!selectedPrice) {
          throw new Error(
            'Selected provider is not configured for this product',
          );
        }

        analytics.trackCheckoutInitiated(productCode);

        const appInstanceId =
          await appInstanceService.getOrCreateCurrentAppInstanceId();
        const session = await triggerPostPaymentsCheckoutSessions({
          productCode,
          appInstanceId,
          provider: selectedProvider,
          ...(checkoutPaymentMethodType
            ? { paymentMethodType: checkoutPaymentMethodType }
            : {}),
          surface: 'premium_page',
        });

        rememberActiveCheckout(session.sessionId);
        postEvent('invoice_created', {
          product_code: productCode,
          plan_id: selectedPrice.legacyPlanId,
          plan_days: selectedProduct.durationDays,
          plan_price: selectedPrice.amount,
          provider: session.provider,
          surface: 'premium_page',
        });

        if (
          session.presentation?.kind === 'telegram_invoice_link' &&
          invoice.open.isAvailable()
        ) {
          const status = await invoice.open(session.presentation.url, 'url');
          if (status === 'paid') {
            const confirmedSession = await getPaymentsCheckoutSessionsById({
              sessionId: session.sessionId,
            });
            const finalSession = terminalStatuses.has(confirmedSession.status)
              ? confirmedSession
              : await pollCheckoutSession(session.sessionId);

            if (finalSession.status === 'succeeded') {
              analytics.trackPaymentSuccess(productCode);
            } else {
              analytics.trackPaymentFailed(
                productCode,
                finalSession.failureCode ||
                  finalSession.status ||
                  'not_succeeded',
              );
            }

            await handleTerminalCheckout(finalSession);
          } else {
            clearActiveCheckout();
            analytics.trackPaymentFailed(
              productCode,
              status || 'user_cancelled',
            );
          }
          return;
        }

        if (session.presentation?.kind === 'external_url') {
          const confirmationUrl = session.presentation.url;

          // Surface the tappable link FIRST, unconditionally. openLink returns
          // void and cannot report whether the host acted, so this is not a
          // fallback for a detected failure — it is the guaranteed path, armed
          // before we even try. When openLink works the user leaves and never
          // sees it.
          setPendingCheckoutUrl(confirmationUrl);

          // One line per attempt, carrying the three checks postEvent itself
          // branches on. Without it we cannot separate "transport missing"
          // (throws; the navigation below handles it) from "transport present
          // but inert" (returns cleanly; only the anchor helps) — and that
          // ambiguity is precisely what made the previous fix a guess.
          const win = window as typeof window & {
            TelegramWebviewProxy?: { postEvent?: unknown };
            external?: { notify?: unknown };
          };
          const attempt = new Error(
            `checkout open attempt — iframe=${window.self !== window.top} proxy=${typeof win.TelegramWebviewProxy?.postEvent === 'function'} notify=${typeof win.external?.notify === 'function'}`,
          );
          attempt.name = 'CheckoutLinkOpenAttempt';
          sendCrashReport(attempt);

          // Open OUTSIDE the Telegram webview: the SberPay/СБП confirmation
          // pages launch bank apps via custom-scheme deep links (sberpay://,
          // bank100…://), which in-webview navigation refuses
          // (net::ERR_UNKNOWN_URL_SCHEME). The system browser handles the
          // deep link; the purchase completes in-app via the stored-session
          // polling when the user returns.
          //
          // The fallback is NOT belt-and-braces — it is the whole payment
          // funnel for the degraded population. `openLink` is a raw postEvent
          // call, and postEvent throws UnknownEnvError whenever none of its
          // three transports (iframe parent / TelegramWebviewProxy /
          // external.notify) is present. That is the EXACT condition
          // createTransportProbe reports as TmaTransportUnavailable, which
          // fired on ~40% of mini-app launches (≈3k/day against ~9k
          // sign-in/tma) for 2026-09-01..04. Every one of those taps created a
          // real YooKassa payment and then never opened the page to pay it:
          // 244 of ~250 checkout failures in that window were
          // `expired_on_confirmation`, and YooKassa conversion sat at ZERO
          // from 09-01 while users retried 2.5–3.7x each.
          if (
            !safeTma('premium.openLink.checkout', () =>
              openLink(confirmationUrl),
            )
          ) {
            // REPORT BEFORE NAVIGATING, and not only through analytics.
            // Pre-fix, the raw throw reached the outer catch and at least
            // logged payment_failed/system_error; safeTma swallows it, and
            // beforeSend suppresses UnknownEnvError for Sentry — so without
            // this the funnel-critical failure would go completely dark.
            // sendCrashReport is the leg that actually survives: it posts via
            // fetch({keepalive:true}), so the request outlives the navigation
            // below, and it deliberately does NOT skip UnknownEnvError-class
            // errors. Its own dedup caps this at 3 per signature per page
            // load, so a rage-tapping user cannot flood it. The analytics
            // call is best-effort by comparison — window.ym fires
            // synchronously, but its /tracking/ym-goal leg is an axios POST
            // behind dynamic imports that the navigation will cancel.
            analytics.trackPaymentFailed(
              productCode,
              'tma_transport_unavailable',
            );
            const openFailure = new Error(
              'Checkout confirmation link could not be opened via Telegram; navigating instead',
            );
            openFailure.name = 'CheckoutLinkOpenFailed';
            sendCrashReport(openFailure);

            // Navigate rather than window.open: we are past an await, so a
            // popup is not user-gesture-backed and mobile blocks it. Losing
            // the SPA costs little — premium is granted by the webhook, not
            // by this tab, and rememberActiveCheckout() has already persisted
            // the session id, so reopening /premium on THIS origin resumes
            // and refreshes the UI. (Not via the return_url: /payment-return
            // is static HTML that ignores ?checkoutSessionId entirely and
            // never boots the SPA — resumption is localStorage-only.)
            //
            // Reaching here means postEvent found no Telegram transport, so
            // this is a plain browser context, NOT a live webview — the
            // bank's custom-scheme deep link is the OS's to handle and the
            // ERR_UNKNOWN_URL_SCHEME trap above does not apply.
            window.location.assign(confirmationUrl);
          }
          return;
        }

        throw new Error(
          'Checkout presentation is not supported on this surface',
        );
      } catch (error) {
        // Branch on the code alone — it is specific enough, and it is the only
        // field present on both error shapes getApiErrorInfo can return.
        const { code, details } = getApiErrorInfo(error);

        if (code === SUBSCRIPTION_PURCHASE_PENDING_CODE) {
          analytics.trackPaymentFailed(productCode, 'purchase_pending');

          emitRef.current('info', {
            title: 'Покупка не завершена',
            description:
              'Вы уже начали оформление — завершите оплату, чтобы подключить Premium.',
          });

          return;
        }

        if (code === SUBSCRIPTION_ALREADY_ACTIVE_CODE) {
          analytics.trackPaymentFailed(productCode, 'already_subscribed');

          const periodEnd = formatPeriodEnd(details?.currentPeriodEndAt);

          emitRef.current('info', {
            title: 'Подписка уже активна',
            description: periodEnd
              ? `Premium активен до ${periodEnd}. Оплачивать повторно не нужно.`
              : 'У вас уже есть активная подписка. Оплачивать повторно не нужно.',
          });

          // NOT clearActiveCheckout(): this attempt never stored an id (the
          // 409 precedes rememberActiveCheckout), so the only id it could drop
          // belongs to an earlier checkout that the mount effect still needs to
          // resume. Refresh instead, so the buy button gates itself.
          //
          // Fired after the toast and never allowed to reject: onBuy runs as
          // `void onBuy(...)` inside a catch, so awaiting a rejecting trigger/
          // mutate here would swallow the message AND raise an unhandled
          // rejection. The refresh is an improvement, not a precondition.
          void Promise.all([
            refreshAuthState(),
            refreshCurrentSubscription(),
          ]).catch(() => undefined);

          return;
        }

        analytics.trackPaymentFailed(productCode, 'system_error');

        console.error(error);

        emitRef.current('error', {
          title: 'Ошибка',
          description: 'Ошибка при создании счета, попробуйте позже',
        });
      }
    },
    [
      checkoutPaymentMethodType,
      clearActiveCheckout,
      getSelectedPrice,
      handleTerminalCheckout,
      pollCheckoutSession,
      products,
      refreshAuthState,
      refreshCurrentSubscription,
      rememberActiveCheckout,
      selectedProvider,
      triggerPostPaymentsCheckoutSessions,
    ],
  );

  const onBuyRef = useRef(onBuy);

  useEffect(() => {
    onBuyRef.current = onBuy;
  }, [onBuy]);

  const translatePlanName = (name: string) => {
    switch (name) {
      case 'Monthly':
        return '1 месяц';
      case 'Quarterly':
        return '3 месяца';
      case 'Annual':
        return '12 месяцев';
      default:
        return name;
    }
  };

  // A live auto-renewing subscription makes a second recurring checkout a
  // guaranteed 409, so don't offer it: without this the user can tap «Подключить»
  // indefinitely and only ever get a toast back.
  //
  // Scoped to the provider actually being sent. Server-side the duplicate guard
  // runs ONLY for yookassa (and a different one for rustore_pay); a
  // telegram_stars checkout has no duplicate guard at all. Gating Stars here
  // would kill purchases the server accepts — and when the yookassa flags are
  // off, `paymentMethod` falls through to telegram_stars for everyone, so every
  // subscriber would face a permanently dead paywall. Other providers fail open
  // to the server guard plus the 409 toast above.
  const purchaseBlocked =
    selectedProvider === 'yookassa' &&
    blocksNewRecurringPurchase(currentSubscription);

  // With the button disabled the toasts are only reachable in the load-then-tap
  // race, so whatever the user needs to know has to live on the button itself.
  const blockedUntil = purchaseBlocked
    ? formatPeriodEnd(currentSubscription?.expiresAt)
    : null;
  // Mirrors the server's own split (alreadySubscribedError): a pending row with
  // no paid period is an ABANDONED purchase, not coverage. Without this the
  // label would tell such a user «Подписка уже активна» while the button that
  // could say otherwise is disabled — the original bug in miniature. Reachable
  // via RuStore, which inserts auto_renewing + pending with no period end at
  // checkout start, and rankSubscriptionSummary ranks PENDING ahead of a live
  // ACTIVE row, so that is exactly what /subscriptions/current hands back.
  const blockedByPendingPurchase =
    purchaseBlocked &&
    currentSubscription?.status === 'pending' &&
    !currentSubscription.expiresAt;

  useEffect(() => {
    if (selectedProduct) {
      setBottomSheet(
        <BottomSheet
          buttons={[
            // Shown only once a checkout exists. A plain anchor, deliberately:
            // it is the one payment path that survives a dead or inert
            // Telegram transport, an ignored web_app_open_link, and a lost
            // user-activation window — all of which look identical from here
            // (openLink returns void and throws nothing).
            ...(pendingCheckoutUrl
              ? [
                  <a
                    key="premium-checkout-link"
                    href={pendingCheckoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-test-id="premium-checkout-link"
                    style={{
                      display: 'block',
                      padding: '14px 16px',
                      marginBottom: 8,
                      borderRadius: 12,
                      textAlign: 'center',
                      fontWeight: 600,
                      color: '#fff',
                      background: SBER_GREEN,
                      textDecoration: 'none',
                    }}
                  >
                    Открыть страницу оплаты
                  </a>,
                ]
              : []),
            <Button
              testId="premium-buy-button"
              variant="premiumGradient"
              loading={isMutating || isResumingCheckout}
              disabled={purchaseBlocked}
              onClick={() => {
                if (purchaseBlocked) {
                  return;
                }

                void onBuyRef.current(selectedProduct.productCode);
              }}
            >
              {!purchaseBlocked
                ? 'Подключить'
                : blockedByPendingPurchase
                  ? 'Завершите оплату'
                  : blockedUntil
                    ? `Premium активен до ${blockedUntil}`
                    : 'Подписка уже активна'}
            </Button>,
          ]}
        />,
      );
    } else {
      setBottomSheet(null);
    }
  }, [
    blockedByPendingPurchase,
    blockedUntil,
    isMutating,
    isResumingCheckout,
    pendingCheckoutUrl,
    purchaseBlocked,
    selectedProduct,
    setBottomSheet,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gridGap: 12,
      }}
    >
      <Card testId="premium-tariffs">
        {products.map((product) => {
          const price = getSelectedPrice(product);
          const testIdSuffix = price?.legacyPlanId || product.productCode;

          return (
            <TariffCard
              key={product.productCode}
              testId={`premium-tariff-${testIdSuffix}`}
              name={translatePlanName(product.name)}
              price={price?.amount || 0}
              currency={price?.currency}
              selected={selectedProductCode === product.productCode}
              onClick={() => onTariffSelect(product.productCode)}
            />
          );
        })}
      </Card>
      {(sberpayAvailable || sbpAvailable || cardPaymentsAvailable) && (
        <Card testId="premium-payment-methods">
          <Heading level={2} compact style={{ paddingTop: 0 }}>
            Способ оплаты
          </Heading>
          {/* СБП leads (product decision: the default saved-method option
              renders first), SberPay second. */}
          {sbpAvailable && (
            <PaymentMethodCard
              testId="premium-payment-method-sbp"
              name="СБП"
              sign={
                <span style={{ fontSize: 10, letterSpacing: 0.5 }}>СБП</span>
              }
              selected={paymentMethod === 'sbp'}
              onClick={() => setChosenPaymentMethod('sbp')}
            />
          )}
          {sberpayAvailable && (
            <PaymentMethodCard
              testId="premium-payment-method-sberbank"
              name="SberPay"
              sign={<CheckIcon style={{ color: SBER_GREEN }} />}
              selected={paymentMethod === 'sberbank'}
              onClick={() => setChosenPaymentMethod('sberbank')}
            />
          )}
          {/* Plain-card one-offs are a fallback only: the saved-method
              options are THE card path when the shop's recurring capability
              is on (product decision — subscriptions are the business). */}
          {!sberpayAvailable && !sbpAvailable && cardPaymentsAvailable && (
            <PaymentMethodCard
              testId="premium-payment-method-yookassa"
              name="Банковская карта"
              sign="₽"
              selected={paymentMethod === 'yookassa'}
              onClick={() => setChosenPaymentMethod('yookassa')}
            />
          )}
          <PaymentMethodCard
            testId="premium-payment-method-telegram_stars"
            name="Звёзды Telegram"
            sign={<StarIcon />}
            selected={paymentMethod === 'telegram_stars'}
            onClick={() => setChosenPaymentMethod('telegram_stars')}
          />
          {(paymentMethod === 'sberbank' ||
            paymentMethod === 'sbp' ||
            paymentMethod === 'yookassa') && (
            <div
              data-test-id="premium-autorenew-disclosure"
              style={{
                color: 'var(--text-secondary-transparent)',
                fontSize: 12,
                lineHeight: '16px',
                letterSpacing: 0.2,
                padding: '4px 0 8px',
              }}
            >
              Подписка продлевается автоматически по сохранённому способу
              оплаты. Управлять подпиской и отменить продление можно в любой
              момент: Профиль → Платежи.
            </div>
          )}
          {/* YooKassa docs: with save_payment_method on, the СБП payment
              page lists only banks that support СБП autopayments — warn the
              user so a missing bank reads as expected, not broken. */}
          {paymentMethod === 'sbp' && (
            <div
              data-test-id="premium-sbp-banks-note"
              style={{
                color: 'var(--text-secondary-transparent)',
                fontSize: 12,
                lineHeight: '16px',
                letterSpacing: 0.2,
                padding: '0 0 8px',
              }}
            >
              В списке будут только банки, поддерживающие подписки через СБП.
              Если вашего банка нет — выберите другой способ оплаты.
            </div>
          )}
        </Card>
      )}
      {/* Informational only — nothing is gated on this line. The document is
          served by the backend outside the SPA, so open it externally. */}
      <div
        data-test-id="premium-oferta-note"
        style={{
          color: 'var(--text-secondary-transparent)',
          fontSize: 12,
          lineHeight: '16px',
          letterSpacing: 0.2,
          textAlign: 'center',
          padding: '0 16px',
        }}
      >
        Оплачивая, вы принимаете условия{' '}
        <a
          href={OFERTA_URL}
          // target=_blank so the href fallback opens a new context instead of
          // navigating the mini app away from the paywall mid-purchase.
          target="_blank"
          rel="noopener noreferrer"
          data-test-id="premium-oferta-link"
          style={{ color: 'inherit', textDecoration: 'underline' }}
          onClick={(event) => {
            // Native href is the fallback when Telegram cannot open the link.
            if (
              safeTma('premium.openLink.oferta', () => openLink(OFERTA_URL))
            ) {
              event.preventDefault();
            }
          }}
        >
          оферты
        </a>
      </div>
      {/* Same visibility rule as the cabinet itself: an expired/stopped row
          must not produce a link that lands on «Подписка не оформлена». */}
      {isBillingSubscriptionVisible(currentSubscription) && (
        <Card
          testId="premium-manage-subscription"
          style={{ padding: '0 16px' }}
        >
          <Link
            to="/profile/payments"
            data-test-id="premium-manage-subscription-link"
          >
            <Cell
              testId="premium-manage-subscription"
              title="Управление подпиской"
              suffixIcon={<ChevroneRightIcon />}
            />
          </Link>
        </Card>
      )}
      <Card testId="premium-advantages">
        <Heading level={2} compact style={{ paddingTop: 0 }}>
          Преимущества
        </Heading>
        <AdvantageCard
          testId="premium-advantage-likes"
          icon={
            <CachedImage
              testId="cached-image"
              alt="likes"
              src="/icons/3d-icons/likes.png"
            />
          }
          text="Смотрите кто лайкает вас"
        />
        {isSuperlikeEnabled && (
          <AdvantageCard
            testId="premium-advantage-superlikes"
            icon={
              <CachedImage
                testId="cached-image"
                alt="superlikes"
                src="/icons/3d-icons/likes.png"
              />
            }
            text="Больше суперлайков каждый день"
          />
        )}
        <AdvantageCard
          testId="premium-advantage-filter"
          icon={
            <CachedImage
              testId="cached-image"
              alt="filter"
              src="/icons/3d-icons/filter.png"
            />
          }
          text="Расширенные фильтры в поиске мэтча"
        />
        <AdvantageCard
          testId="premium-advantage-return"
          icon={
            <CachedImage
              testId="cached-image"
              alt="return"
              src="/icons/3d-icons/return.png"
            />
          }
          text="Возвращайте профили, которые вы отклонили"
        />
        <AdvantageCard
          testId="premium-advantage-endless"
          icon={
            <CachedImage
              testId="cached-image"
              alt="endless"
              src="/icons/3d-icons/endless.png"
            />
          }
          text="Бесконечный свайп анкет"
        />
        <AdvantageCard
          testId="premium-advantage-badge"
          icon={
            <CachedImage
              testId="cached-image"
              alt="badge"
              src="/icons/3d-icons/fire.png"
            />
          }
          text="Специальный бейдж у профиля"
        />
      </Card>
    </div>
  );
};

export default Premium;
